export type ChartSnapshot = {
  id: string;
  collectedAt: string;
  commitTime: string | null;
  commitSha: string | null;
  /** Opaque cloud revision identity, for grouping only; never displayed as a commit SHA. */
  revisionRef?: string | null;
  profile: string;
  status: string;
  provenance: string;
  scopeHash?: string;
  sources?: {adapter:string;version:string|null;status:string;warnings:string[];methodId:string}[];
  sourceKind?: string;
  metricMethods?: Record<string, string | null>;
  metricStates?: Record<string, string>;
  aggregates: Record<string, number | null>;
  commitSubject?: string | null;
  skippedSincePrevious?: number | null;
};

export type ChartRow = {
  sourceKind?: string;
  t: number;
  id: string;
  label: string;
  profile: string;
  status: string;
  provenance: string;
  collectedAt: string;
  commitTime: string | null;
  commitSha: string | null;
  scopeHash?: string;
  sources?: ChartSnapshot["sources"];
  sourceLoc: number | null;
  testLoc: number | null;
  fileCount: number | null;
  functionCount: number | null;
  ccAbove: number | null;
  cognitiveAbove: number | null;
  largeFunctions: number | null;
  p95Cyclomatic: number | null;
  p50Cyclomatic: number | null;
  maxCyclomatic: number | null;
  ccAbovePct: number | null;
  cognitiveAbovePct: number | null;
  largeFunctionsPct: number | null;
  ccPerKloc: number | null;
  cognitivePerKloc: number | null;
  cloneGroups: number | null;
  duplicatedLines: number | null;
  duplicationPct: number | null;
  knipUnusedFiles: number | null;
  knipUnusedExports: number | null;
  knipUnusedDeps: number | null;
  depcruiseCycles: number | null;
  fallowCycles: number | null;
  oxlintWarnings: number | null;
  oxlintErrors: number | null;
  compilerErrors: number | null;
  reactDoctorFindings: number | null;
  qltySmells: number | null;
  qltyCodeSmells: number | null;
  cycles: number | null;
  isMethodBreak?: boolean;
  methods?: Record<string, string | null>;
  commitSubject?: string | null;
  skippedSincePrevious?: number | null;
};

const COMPLEXITY = new Set([
  "ccAbove",
  "cognitiveAbove",
  "largeFunctions",
  "cloneGroups",
  "duplicatedLines",
  "functions",
  "functionCount",
  "p50Cyclomatic",
  "maxCyclomatic",
  "ccAbovePct",
  "cognitiveAbovePct",
  "largeFunctionsPct",
  "ccPerKloc",
  "cognitivePerKloc",
]);

export function snapshotTimeMs(snapshot: ChartSnapshot): number {
  // A mutable worktree is an observation at collection time, even when it has a HEAD.
  const raw = snapshot.sourceKind === "worktree" || snapshot.provenance === "working_tree"
    ? snapshot.collectedAt
    : snapshot.commitTime || snapshot.collectedAt;
  const t = Date.parse(raw);
  return Number.isFinite(t) ? t : Date.parse(snapshot.collectedAt);
}

export function metricValue(snapshot: ChartSnapshot, key: string): number | null {
  const sourceKey = key === "functionCount" ? "functions" : key;
  const state = snapshot.metricStates?.[sourceKey];
  if (state === "partial" || state === "unavailable" || state === "not_applicable" || state === "not_requested") return null;
  const value = snapshot.aggregates[sourceKey];
  if (value == null) return null;
  if (
    COMPLEXITY.has(key) &&
    snapshot.status !== "complete" &&
    (snapshot.aggregates.functions == null || snapshot.aggregates.functions === 0)
  ) {
    return null;
  }
  return value;
}

export function healthChartRows(snapshots: ChartSnapshot[]): ChartRow[] {
  const rows: ChartRow[] = [...snapshots]
    .sort((a, b) => snapshotTimeMs(a) - snapshotTimeMs(b) || a.collectedAt.localeCompare(b.collectedAt))
    .map((snapshot) => {
      const sha = snapshot.commitSha ? snapshot.commitSha.slice(0, 8) : snapshot.profile;
      const when = new Date(snapshotTimeMs(snapshot)).toLocaleDateString();
      return {
        sourceKind:snapshot.sourceKind,
        t: snapshotTimeMs(snapshot),
        id: snapshot.id,
        label: `${when} ${sha}`,
        profile: snapshot.profile,
        status: snapshot.status,
        provenance: snapshot.provenance,
        collectedAt: snapshot.collectedAt,
        commitTime: snapshot.commitTime,
        commitSha: snapshot.commitSha,
        scopeHash: snapshot.scopeHash,
        sources: snapshot.sources,
        sourceLoc: metricValue(snapshot, "sourceLoc"),
        testLoc: metricValue(snapshot, "testLoc"),
        fileCount: metricValue(snapshot, "fileCount"),
        functionCount: metricValue(snapshot, "functionCount"),
        ccAbove: metricValue(snapshot, "ccAbove"),
        cognitiveAbove: metricValue(snapshot, "cognitiveAbove"),
        largeFunctions: metricValue(snapshot, "largeFunctions"),
        p95Cyclomatic: metricValue(snapshot,"p95Cyclomatic"),
        p50Cyclomatic: metricValue(snapshot,"p50Cyclomatic"),
        maxCyclomatic: metricValue(snapshot,"maxCyclomatic"),
        ccAbovePct: metricValue(snapshot,"ccAbovePct"),
        cognitiveAbovePct: metricValue(snapshot,"cognitiveAbovePct"),
        largeFunctionsPct: metricValue(snapshot,"largeFunctionsPct"),
        ccPerKloc: metricValue(snapshot, "ccPerKloc"),
        cognitivePerKloc: metricValue(snapshot, "cognitivePerKloc"),
        cloneGroups: metricValue(snapshot, "cloneGroups"),
        duplicatedLines: metricValue(snapshot, "duplicatedLines"),
        duplicationPct: metricValue(snapshot, "duplicationPct"),
        knipUnusedFiles: metricValue(snapshot, "knipUnusedFiles"),
        knipUnusedExports: metricValue(snapshot, "knipUnusedExports"),
        knipUnusedDeps: metricValue(snapshot, "knipUnusedDeps"),
        depcruiseCycles: metricValue(snapshot, "depcruiseCycles"),
        fallowCycles: metricValue(snapshot, "fallowCycles"),
        oxlintWarnings: metricValue(snapshot, "oxlintWarnings"),
        oxlintErrors: metricValue(snapshot, "oxlintErrors"),
        compilerErrors: metricValue(snapshot, "compilerErrors"),
        reactDoctorFindings: metricValue(snapshot, "reactDoctorFindings"),
        qltyCodeSmells: metricValue(snapshot,"qltyCodeSmells"),
        qltySmells: metricValue(snapshot, "qltySmells"),
        cycles: metricValue(snapshot, "cycles"),
        methods: snapshot.metricMethods ?? {},
        commitSubject: snapshot.commitSubject ?? null,
        skippedSincePrevious: snapshot.skippedSincePrevious ?? null,
      };
    });
  // Recharts connects adjacent numeric values. Insert a null-only boundary point before
  // a new method so a changed config/scope never appears as a smooth improvement.
  const metricKeys: (keyof ChartRow)[] = ["sourceLoc", "testLoc", "fileCount", "functionCount", "ccAbove", "cognitiveAbove", "largeFunctions", "p95Cyclomatic", "p50Cyclomatic", "maxCyclomatic", "ccAbovePct", "cognitiveAbovePct", "largeFunctionsPct", "ccPerKloc", "cognitivePerKloc", "cloneGroups", "duplicatedLines", "duplicationPct", "knipUnusedFiles", "knipUnusedExports", "knipUnusedDeps", "depcruiseCycles", "fallowCycles", "oxlintWarnings", "oxlintErrors", "compilerErrors", "reactDoctorFindings", "qltySmells", "qltyCodeSmells", "cycles"];
  const output: ChartRow[] = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]!;
    const previous = rows[i - 1];
    const currentSnapshot = snapshots.find((s) => s.id === row.id);
    const previousSnapshot = previous ? snapshots.find((s) => s.id === previous.id) : undefined;
    const changed = metricKeys.filter((key) => {
      const metric = String(key);
      return previous && typeof row[key] === "number" && typeof previous[key] === "number" &&
        currentSnapshot?.metricMethods?.[metric] !== previousSnapshot?.metricMethods?.[metric];
    });
    if (changed.length) {
      const boundary = { ...row, id: `${row.id}:method-break`, label: `${row.label} method changed`, isMethodBreak: true };
      for (const key of changed) (boundary[key] as number | null) = null;
      output.push(boundary);
    }
    const legacyKeys = metricKeys.filter((key) => currentSnapshot?.metricStates?.[String(key)] === "legacy_unverified");
    const legacy = legacyKeys.length > 0;
    if (legacy) output.push({ ...row, id: `${row.id}:legacy-before`, label: `${row.label} legacy method`, isMethodBreak: true, ...Object.fromEntries(legacyKeys.map((key) => [key, null])) } as ChartRow);
    output.push(row);
    if (legacy) output.push({ ...row, id: `${row.id}:legacy-after`, label: `${row.label} legacy method`, isMethodBreak: true, ...Object.fromEntries(legacyKeys.map((key) => [key, null])) } as ChartRow);
  }
  return output;
}

const FALLOW_TREND_KEYS = new Set([
  "ccAbove", "cognitiveAbove", "largeFunctions", "cloneGroups", "duplicatedLines", "duplicationPct",
  "ccAbovePct", "cognitiveAbovePct", "largeFunctionsPct", "ccPerKloc", "cognitivePerKloc",
]);

/** Chart grouping: lockfile/tsconfig edits must not shatter a complexity trend. Scope (via LOC method) still splits recipes. */
export function chartRecipe(row: Pick<ChartRow, "methods">, key: string): string {
  const method = row.methods?.[key] ?? "";
  if (FALLOW_TREND_KEYS.has(key)) {
    const review = method.match(/:(review-.+)$/)?.[1] ?? "complexity";
    return `scope:${row.methods?.sourceLoc ?? method}:${review}`;
  }
  return method;
}

export function seriesContinuity(rows: ChartRow[], key: keyof ChartRow): { points: number; segments: number } {
  let points = 0;
  let segments = 0;
  let inSegment = false;
  for (const row of rows) {
    if (row.isMethodBreak) {
      inSegment = false;
      continue;
    }
    const has = typeof row[key] === "number";
    if (has) {
      points += 1;
      if (!inSegment) segments += 1;
    }
    inSegment = has;
  }
  return { points, segments };
}

export function countedPoints(rows: ChartRow[], keys: (keyof ChartRow)[]): number {
  return new Set(rows.filter((row) => !row.isMethodBreak && keys.some((key) => typeof row[key] === "number")).map(row=>row.sourceKind==="worktree"||row.provenance==="working_tree"?row.id:row.commitSha??row.id)).size;
}

/** Collection retries are evidence, not additional revisions on the timeline. */
export function revisionSnapshots(snapshots: ChartSnapshot[]): ChartSnapshot[] {
  const selected = new Map<string, ChartSnapshot>();
  for (const snapshot of [...snapshots].sort((a,b) => a.collectedAt.localeCompare(b.collectedAt))) {
    const revision = snapshot.commitSha ?? snapshot.revisionRef;
    const key = snapshot.sourceKind === "worktree" || !revision ? snapshot.id : `${revision}:${snapshot.profile}`;
    selected.set(key, snapshot);
  }
  return [...selected.values()];
}

/** One shared x viewport for every band: without it each axis auto-scales to its own grid, misaligning bars and events with the lines. */
export function sharedDomain(times: number[], start: number, end: number, dataMin: number, dataMax: number, axisExtras: number[]): { min: number | undefined; max: number | undefined } {
  if (times.length === 1) return { min: start - 43200000, max: end + 43200000 };
  const lo = Math.min(dataMin, ...axisExtras);
  const hi = Math.max(dataMax, ...axisExtras);
  return { min: Number.isFinite(lo) ? lo : undefined, max: Number.isFinite(hi) ? hi : undefined };
}

/** Axis normalization for the unified timeline: units collapse into a bounded set of axis groups. */
export function axisGroup(unit: string): string {
  if (unit === "lines") return "lines";
  if (unit === "%") return "%";
  if (unit === "per 1k lines") return "per 1k";
  if (unit === "complexity") return "complexity";
  return "count";
}

export const AXIS_ORDER = ["lines", "count", "per 1k", "%", "complexity"];

export function axisGroupsFor(lines: { unit: string }[]): string[] {
  const present = new Set(lines.map((line) => axisGroup(line.unit)));
  return AXIS_ORDER.filter((group) => present.has(group));
}

export function axisLayout(groups: string[]): { group: string; position: "left" | "right"; offset: number }[] {
  if (groups.length <= 1) return groups.map((group) => ({ group, position: "left" as const, offset: 0 }));
  let left = 0, right = 0;
  return groups.map((group) => {
    const preferLeft = group === "lines" || group === "per 1k" || group === "complexity";
    const position = preferLeft ? "left" as const : "right" as const;
    const offset = (position === "left" ? left++ : right++) * 52;
    return { group, position, offset };
  });
}
